import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { bookingService } from "../../services/bookingService";
import { toast } from "react-toastify";
import Layout from "../layout/layout";
import Sidebar from "../profile-sidebar/profileSidebar";
import moment from "moment";

const CancelPendingBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const data = await bookingService.getCancelPendingBookings(
          currentPage,
          pageSize
        );
        setBookings(data.data.items ? data.data.items : []);
        setTotalItems(data.data.totalRecords || 0);
      } catch (error) {
        console.error("Error fetching bookings:", error);
        toast.error("Không thể tải danh sách booking yêu cầu hủy.");
        setBookings([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [currentPage, pageSize]);

  const handleApproveCancel = async () => {
    setLoading(true);
    try {
      const response = await bookingService.approveCancelBooking(
        selectedBookingId
      );
      toast.success(response.data.message);
      const data = await bookingService.getCancelPendingBookings(
        currentPage,
        pageSize
      );
      setBookings(data.data.items ? data.data.items : []);
      setTotalItems(data.data.totalRecords || 0);
      setShowModal(false); // Đóng modal sau khi phê duyệt
    } catch (error) {
      console.error("Error approving cancel:", error);
      toast.error(error.response?.data?.message || "Không thể phê duyệt hủy.");
    } finally {
      setLoading(false);
    }
  };

  const openConfirmModal = (bookingId) => {
    setSelectedBookingId(bookingId);
    setShowModal(true);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatCurrencyVND = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  return (
    <Layout>
      <div className="content">
        <div className="container">
          <div className="row">
            <Sidebar page="cancel-pending-bookings" />
            <div className="col-xl-9 col-lg-8">
              <div className="card shadow-none mb-0">
                <div className="card-header">
                  <h6>Danh sách yêu cầu hủy Booking</h6>
                </div>
                <div className="card-body">
                  {loading ? (
                    <div className="d-flex justify-content-center align-items-center">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                    </div>
                  ) : bookings.length === 0 ? (
                    <p>Không có booking nào yêu cầu hủy.</p>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>ID Booking</th>
                              <th>Tên tour</th>
                              <th>Tên khách hàng</th>
                              <th>Liên hệ</th>
                              <th>Ngày đặt</th>
                              <th>Ngày bắt đầu</th>
                              <th>Ngày kết thúc</th>
                              <th>Tổng tiền</th>
                              <th>Lý do hủy</th>
                              <th>Trạng thái</th>
                              <th>Hành động</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bookings.map((booking) => (
                              <tr key={booking.bookingId}>
                                <td>{booking.bookingId}</td>
                                <td>{booking.tourName}</td>
                                <td>{booking.userName}</td>
                                <td>{booking.phoneNumber}</td>
                                <td>
                                  {moment(booking.bookingDate).format(
                                    "DD-MM-YYYY"
                                  )}
                                </td>
                                <td>
                                  {moment(booking.startDate).format(
                                    "DD-MM-YYYY"
                                  )}
                                </td>
                                <td>
                                  {moment(booking.endDate).format("DD-MM-YYYY")}
                                </td>
                                <td>{formatCurrencyVND(booking.totalPrice)}</td>
                                <td>
                                  {booking.reasonCancelBooking ||
                                    "Không có lý do"}
                                </td>
                                <td>
                                  {booking.status === "Pending"
                                    ? "Chờ phê duyệt"
                                    : ""}
                                </td>
                                <td>
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={() =>
                                      openConfirmModal(booking.bookingId)
                                    }
                                    disabled={loading}
                                  >
                                    Phê duyệt
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <p>
                          Hiển thị {bookings.length} / {totalItems} booking
                        </p>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || loading}
                          >
                            Trang trước
                          </button>
                          <span>
                            Trang {currentPage} / {totalPages}
                          </span>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || loading}
                          >
                            Trang sau
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal xác nhận */}
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Xác nhận phê duyệt hủy</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Bạn có chắc chắn muốn phê duyệt hủy booking này không?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  Hủy
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleApproveCancel}
                  disabled={loading}
                >
                  {loading ? "Đang xử lý..." : "Xác nhận"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CancelPendingBookings;
